import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, ResponsiveFlex, Tooltip } from "@narmi/design_system";
import { ProgressButtons, useNotificationContext } from "cerulean";
import { entities, modules } from "byzantine";
import { useLocalization } from "@fluent/react";
import * as formAdapters from "../../../src/adapters";
import HeaderWithSteps from "../../HeaderWithSteps";
import { useSudoContext } from "../../SudoContext";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import {
  RecipientDrawer,
  DRAWER_TYPES,
  SECTION_NAMES,
} from "../../RecipientDrawer";
import RecipientSelector from "../RecipientSelector";
import {
  ACH_PAYMENT_ROUTE,
  ACH_PAYMENT_DETAIL_ROUTE,
} from "./ACHPaymentRoutes";
import styles from "./PaymentScreen.module.scss";

export const INITIAL_TOTAL_STEPS = 4;
// instant delivery skips the additional details step, so the flow is one step shorter
export const INSTANT_TOTAL_STEPS = INITIAL_TOTAL_STEPS - 1;
type PaymentType = "send" | "collect";

const RecipientSection = ({
  paymentType,
  onPaymentTypeChange,
  showPaymentTypeOptions,
}: {
  paymentType: PaymentType;
  onPaymentTypeChange: (type: PaymentType) => void;
  showPaymentTypeOptions: boolean;
}) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [customAlertCopy, setCustomAlertCopy] = useState<string>("");
  const selectedRecipient = entities.recipients.useSelectedRecipient();
  const achPaymentForm = modules.achPayments.ACHPaymentForm.useForm();
  const recipientForm = entities.recipients.ACHPaymentRecipientForm.useForm();
  const hasManageRecipientPermission =
    modules.achPayments.useManageRecipientPermission();
  // if user doesn't have manage recipient permissions, just show them recipients they can only send ach payments to
  const recipients = modules.achPayments.usePermissionedRecipients();
  const {
    values: { selectedRecipientId },
  } = recipientForm;
  const { onChange, ...restOfFieldProps } = formAdapters.fieldWithOnChange(
    recipientForm,
    "selectedRecipientId"
  );

  const isRecipientWithACHDestinationMissing =
    selectedRecipient && !selectedRecipient.ach_destination;

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  useEffect(() => {
    if (isRecipientWithACHDestinationMissing) {
      setCustomAlertCopy(l10n.getString("ach-payment-ach-details-banner"));
      toggleDrawer();
    }
  }, [isRecipientWithACHDestinationMissing, l10n]);

  const handlePaymentTypeChange = useCallback(
    (nextPaymentType: PaymentType) => {
      if (nextPaymentType === paymentType) {
        return;
      }

      achPaymentForm.resetForm();
      recipientForm.resetForm();
      onPaymentTypeChange(nextPaymentType);
    },
    [achPaymentForm, onPaymentTypeChange, paymentType, recipientForm]
  );

  const handleOnChange = (recipientId: API.RecipientId) => {
    // reset the ach payment form on the following screen if recipient changes
    if (achPaymentForm.dirty && recipientId !== selectedRecipientId) {
      achPaymentForm.resetForm();
      achPaymentForm.setFieldValue(
        "transactionType",
        paymentType === "collect" ? "debit" : "credit"
      );
    }
    onChange(recipientId);
  };

  const handleCloseDrawer = (recipientId?: API.RecipientId) => {
    const updatedRecipient = recipients.find((r) => r.id === recipientId);
    if (updatedRecipient && !updatedRecipient?.ach_destination) {
      handleOnChange("" as API.RecipientId);
      sendNotification({
        type: "negative",
        text: l10n.getString("add-ach-details-banner"),
      });
    } else if (recipientId) {
      handleOnChange(recipientId);
    }

    toggleDrawer();
    setCustomAlertCopy("");
  };

  return (
    <>
      {showPaymentTypeOptions ? (
        <ResponsiveFlex gapSize="m">
          <h4 className="fontSize--m nds-sans">
            {l10n.getString("single-ach-payment-type-title")}
          </h4>
          <Radio
            kind="input-card"
            name="paymentType"
            value="send"
            checked={paymentType === "send"}
            onCheck={() => handlePaymentTypeChange("send")}
          >
            <h4 className="fontFamily--body fontSize--m fontWeight--semibold">
              {l10n.getString("single-ach-payment-type-send-title")}
            </h4>
            <p className="fontSize--s margin--top--xxs fontColor--primary">
              {l10n.getString("single-ach-payment-type-send-description")}
            </p>
          </Radio>
          <Radio
            kind="input-card"
            name="paymentType"
            value="collect"
            checked={paymentType === "collect"}
            onCheck={() => handlePaymentTypeChange("collect")}
          >
            <h4 className="fontFamily--body fontSize--m fontWeight--semibold">
              {l10n.getString("single-ach-payment-type-collect-title")}
            </h4>
            <p className="fontSize--s margin--top--xxs fontColor--primary">
              {l10n.getString("single-ach-payment-type-collect-description")}
            </p>
          </Radio>
        </ResponsiveFlex>
      ) : null}

      <ResponsiveFlex gapSize="m">
        <h4 className="fontSize--m nds-sans">
          {l10n.getString("ach-payment-recipient-title")}
        </h4>
        <div className="recipient-screen__recipient-selector">
          <RecipientSelector
            recipients={recipients}
            field="recipientId"
            label={
              paymentType === "collect"
                ? l10n.getString("single-ach-payment-recipient-collect-from")
                : l10n.getString("single-ach-payment-recipient-send-to")
            }
            onAddRecipientClick={toggleDrawer}
            onChange={handleOnChange}
            displayBottomAction={hasManageRecipientPermission}
            {...restOfFieldProps}
          />
        </div>
      </ResponsiveFlex>
      <RecipientDrawer
        recipientId={selectedRecipientId}
        handleClose={handleCloseDrawer}
        isOpen={isDrawerOpen}
        fromACHFlow={true}
        drawerType={selectedRecipientId ? DRAWER_TYPES.edit : DRAWER_TYPES.add}
        customAlertCopy={customAlertCopy}
        defaultSectionToOpen={customAlertCopy ? SECTION_NAMES.ach : undefined}
      />
    </>
  );
};

const RecipientScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { onSetInitialSteps } = useSudoContext();
  const features = useUserFeatures();
  const isSingleExpediteAvailable =
    modules.achPayments.useIsSingleExpediteAvailable();
  const achPaymentForm = modules.achPayments.ACHPaymentForm.useForm();
  const sendEnabled = !!features.ach_payments;
  const collectEnabled = !!features.ach_payments_collect;
  const showPaymentTypeOptions = sendEnabled && collectEnabled;
  const [paymentType, setPaymentType] = useState<PaymentType>(() => {
    if (showPaymentTypeOptions) {
      return achPaymentForm.values.transactionType === "debit"
        ? "collect"
        : "send";
    }

    if (collectEnabled && !sendEnabled) {
      return "collect";
    }

    return "send";
  });
  let headerText = l10n.getString("ach-payment-payment-title");

  if (showPaymentTypeOptions) {
    headerText = l10n.getString("single-collect-and-send-title");
  } else if (collectEnabled) {
    headerText = l10n.getString("single-ach-payment-collect-title");
  }

  const onSubmitSuccessful = useCallback(() => {
    navigate(`/${ACH_PAYMENT_ROUTE}/${ACH_PAYMENT_DETAIL_ROUTE}`);
  }, [navigate]);

  const handleSubmit = entities.recipients.useHandleRecipientPageSubmit({
    onSubmitSuccessful,
  });

  useEffect(() => {
    onSetInitialSteps(INITIAL_TOTAL_STEPS);
  }, [onSetInitialSteps]);

  useEffect(() => {
    achPaymentForm.setFieldValue(
      "transactionType",
      paymentType === "collect" ? "debit" : "credit"
    );
  }, [paymentType]);

  return (
    <ResponsiveFlex gapSize="xl">
      <HeaderWithSteps
        headerText={headerText}
        icon={
          !isSingleExpediteAvailable ? (
            <Tooltip text={l10n.getString("ach-payment-payment-title-tooltip")}>
              <i className={`narmi-icon-info ${styles.headerInfoIcon}`} />
            </Tooltip>
          ) : undefined
        }
      />

      <RecipientSection
        paymentType={paymentType}
        onPaymentTypeChange={setPaymentType}
        showPaymentTypeOptions={showPaymentTypeOptions}
      />

      <ProgressButtons
        backLabel={l10n.getString("button-cancel")}
        nextLabel={l10n.getString("ach-recipient-selection-form-submit")}
        onBack={() => {
          navigate("/transfers");
        }}
        onNext={handleSubmit}
      />
    </ResponsiveFlex>
  );
};

export default RecipientScreen;
